// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useState } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useFetch } from 'app/utils/hooks';
import { apiErrorHandler, isNotFoundError } from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import { TokenInput } from 'app/components/Core';
import {
  V3ConfirmFormSchema,
  defaultValuesForConfirm,
} from 'app/components/Profile/Ids/ClaimDialogs/utils';
import { getVerificationCodeLink } from 'app/store/Auth/selectors';
import { yupResolver } from '@hookform/resolvers/yup';

export interface ConfirmClaimDialogProps {
  open: boolean;
  requestGuid?: string;
  onClose: () => void;
}

export const ConfirmClaimDialog = ({
  open,
  requestGuid,
  onClose,
}: ConfirmClaimDialogProps) => {
  const dispatch = useAppDispatch();
  const link = useAppSelector(getVerificationCodeLink);
  const [codeErrorMessage, setCodeErrorMessage] = useState('');
  const { callAPI } = useFetch<{ code: string }>();

  const {
    control,
    formState: { isValid, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<{ code: string }>({
    defaultValues: defaultValuesForConfirm,
    mode: 'all',
    resolver: yupResolver(V3ConfirmFormSchema),
  });

  const handleClose = () => {
    onClose();
    reset(defaultValuesForConfirm);
    setCodeErrorMessage('');
  };

  const onSubmit = handleSubmit(async (values: { code: string }) => {
    setCodeErrorMessage('');
    if (link && requestGuid) {
      try {
        await callAPI(link.href.replace('{requestGuid}', requestGuid), {
          code: values.code,
        });

        dispatch(
          addAlertAction({
            message:
              'We will check for any matching society IDs against this email address.',
          })
        );

        handleClose();
        setTimeout(() => window.location.reload(), 2000);
      } catch (e: any) {
        if (isNotFoundError(e)) {
          dispatch(
            addAlertAction({
              message:
                'Code is invalid. Please check your email or contact our support',
            })
          );
          setCodeErrorMessage('Code invalid');
        } else {
          dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
        }
      }
    }
  });

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent data-testid="dialog">
        <ModalHeader data-testid="dialog-title" title="Society Connection">
          Society Connection
        </ModalHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return onSubmit(e);
          }}
        >
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="md" lineHeight="22px" pb={4}>
                We have sent you an email to confirm your address. Please check
                your inbox and enter the code below.
              </Text>
              <Flex px="44px" w="full">
                <Controller
                  control={control}
                  name="code"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <TokenInput
                      onChange={(val) => {
                        if (codeErrorMessage) {
                          setCodeErrorMessage('');
                        }
                        onChange(val);
                      }}
                      error={error?.message || codeErrorMessage}
                    />
                  )}
                />
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack spacing={2} direction="row">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={
                  !isValid || isSubmitting || Boolean(codeErrorMessage)
                }
              >
                Connect
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
